import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import UnifiedHeader from '@/components/common/UnifiedHeader.tsx';
import GlobalSearch from '@/components/features/search/GlobalSearch.tsx';
import TransferPatientsPage from '@/pages/TransferPatientsPage.tsx';
import PEPatientList from '@/components/common/PEPatientListSearchable.tsx';
// Dashboard imports for all 6 roles
import EADashboard from '@/pages/ea/EADashboard.tsx';
import ESDashboard from '@/pages/es/ESDashboard.tsx';
import CADashboard from '@/pages/ca/CADashboard.tsx';
import CSUserDashboard from '@/pages/cs/CSUserDashboard.tsx';
import PADashboard from '@/pages/pa/PADashboard.tsx';
import PEDashboard from '@/pages/pe/PEDashboard.tsx';
import { ContentSection, NavigationState, MainDashboardProps } from '@/types/navigation.ts';

const MainDashboard = ({ userType, initialSection = 'dashboard' }: MainDashboardProps = {}) => {
  const { user } = useAuth();
  const [navigationState, setNavigationState] = useState<NavigationState>({
    currentSection: initialSection
  });

  // Set initial section - always use 'dashboard' for simplicity
  useEffect(() => {
    if (initialSection === 'dashboard') {
      setNavigationState(prev => ({ ...prev, currentSection: 'dashboard' }));
    }
  }, [userType, user?.type, initialSection]);

  const handleNavigate = (section: ContentSection, params?: any) => {
    setNavigationState({
      currentSection: section,
      ...params
    });
  };

  const handlePatientDetails = (patientClinicDataId: number, clinicId?: number) => {
    setNavigationState({
      currentSection: 'patient-details',
      patientClinicDataId,
      clinicId
    });
  };

  const handleBack = () => {
    // Always navigate back to dashboard - simple and clean
    handleNavigate('dashboard');
  };

  const renderContent = () => {
    switch (navigationState.currentSection) {
      case 'global-search':
        return (
          <GlobalSearch
            onBack={handleBack}
          />
        );

      case 'transfer-patients':
        return <TransferPatientsPage
          onBack={handleBack}
          onPatientSelect={(patientClinicDataId, clinicId) => {
            setNavigationState({
              currentSection: 'patient-details',
              patientClinicDataId,
              clinicId,
              isFromTransferPatients: true
            });
          }}
        />;

      case 'followup-patients':
        return (
          <PEPatientList
            type="followup"
          />
        );

      case 'deferred-patients':
        return (
          <PEPatientList
            type="deferred"
          />
        );

      case 'dashboard':
      default:
        // Return appropriate dashboard based on user type - these components have their own headers
        switch (userType || user?.type) {
          case 'EA':
          case 'CA':
          case 'PA':
            // These dashboard components have their own headers, so return them directly
            switch (userType || user?.type) {
              case 'EA':
                return <EADashboard />;
              case 'CA':
                return <CADashboard />;
              case 'PA':
                return <PADashboard />;
            }
            break;
          case 'ES':
            return <ESDashboard onPatientSelect={handlePatientDetails} />;
          case 'CS':
            return <CSUserDashboard />;
          case 'PE':
            return (
              <PEDashboard
                onNavigate={handleNavigate}
                onPatientSelect={handlePatientDetails}
              />
            );
          default:
            return (
              <div className="p-6">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Welcome, {user?.fullName}
                  </h2>
                  <p className="text-gray-600">
                    Dashboard for role "{userType || user?.type}" is being developed.
                  </p>
                </div>
              </div>
            );
        }
    }
  };

  const getHeaderProps = () => {
    const currentUserType = userType || user?.type;
    const baseProps = {
      showFollowUp: userType === 'PE',
      showDeferred: userType === 'PE',
      userType: currentUserType,
      onFollowUpClick: () => handleNavigate('followup-patients'),
      onDeferredClick: () => handleNavigate('deferred-patients'),
      onSearchClick: () => handleNavigate('global-search'),
      showNotificationBell: currentUserType === 'CS' || currentUserType === 'CA',
      onNotificationClick: () => handleNavigate('transfer-patients'),
    };

    switch (navigationState.currentSection) {
      case 'global-search':
        return {
          ...baseProps,
          title: 'Global Patient Search',
          showBackButton: true,
          onBackClick: handleBack,
        };
      case 'transfer-patients':
        return {
          ...baseProps,
          title: 'Transfer Patient List',
          showBackButton: true,
          onBackClick: handleBack,
        };
      case 'followup-patients':
        return {
          ...baseProps,
          title: 'Follow-up Patients',
          showBackButton: true,
          onBackClick: handleBack,
        };
      case 'deferred-patients':
        return {
          ...baseProps,
          title: 'Deferred Patients',
          showBackButton: true,
          onBackClick: handleBack,
        };
      default:
        return {
          ...baseProps,
          title: 'PEP Dashboard',
          showBackButton: false,
        };
    }
  };

  // Check if we're showing a dashboard that has its own header
  const dashboardsWithOwnHeaders = ['EA', 'ES', 'CA', 'CS', 'PA', 'PE'];
  const currentUserType = userType || user?.type;
  const showMainHeader = navigationState.currentSection !== 'dashboard' ||
    !dashboardsWithOwnHeaders.includes(currentUserType);

  return (
    <div className="min-h-screen bg-background">
      {showMainHeader && (
        <UnifiedHeader
          {...getHeaderProps()}
          onNavigate={handleNavigate}
        />
      )}

      <div className="flex-1">
        {renderContent()}
      </div>
    </div>
  );
};

export default MainDashboard;